import React from "react";
import { Draggable } from "react-beautiful-dnd";
import HtmlTooltip from "@material-ui/core/Tooltip";
import { useOnScreen } from "./hooks/useOnScreen";

const DraggablePageImpl = ({
    index,
    item,
    pageImage,
    documentSectionId,
    docName,
    parentId,
    deletedPages,
    isLocked,
    isDocumentLoading,
    k,
    activeImage,
    setSeparator,
    getItemClass,
    getItemStyle,
    getItemStyleInner,
    setZoomPage,
    setZoomOpen
}) => {
    const [ref, isVisible] = useOnScreen({ rootMargin: "300px" });

    const isPlaceholder = pageImage === null;

    let deletedImage = 0;
    let deletedClass = "";
    let disabledClass = "";
    let deleted = 0;

    if (!isPlaceholder && pageImage.includes("deleted:1")) {
        deleted = 1;
        deletedImage = 1;
        deletedClass = "deleted";
        disabledClass = "disabled";
    }

    if (!isPlaceholder) {
        deletedPages?.forEach((delPage) => {
            if (String(delPage) === String(pageImage)) {
                deletedImage = 1;
                deletedClass = "deleted";
                disabledClass = "disabled";
            }
        });
    }

    if (isPlaceholder) {
        return (
            <Draggable
                key={index}
                draggableId={`${k}${index}`}
                index={index}
                isDragDisabled={true}
            >
                {(dragProvided) => (
                    <div
                        className="image-outer"
                        {...dragProvided.dragHandleProps}
                        {...dragProvided.draggableProps}
                        ref={dragProvided.innerRef}
                    >
                        <div
                            id="pdf-image"
                            className="print-preview pdf-image"
                        >
                            <div className="pdf-skeleton-tile" />
                            <p>Page {index + 1}</p>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    }

    return (
        <Draggable
            key={index}
            draggableId={`${k}${index}`}
            index={index}
            isDragDisabled={isLocked || isDocumentLoading}
        >
            {(dragProvided, snapshot) => (
                <div
                    className={`${deletedClass} ${getItemClass(snapshot.isDragging)}`}
                    {...dragProvided.dragHandleProps}
                    {...dragProvided.draggableProps}
                    ref={dragProvided.innerRef}
                    style={getItemStyle(
                        snapshot.isDragging,
                        dragProvided.draggableProps.style
                    )}
                    disable={disabledClass}
                >
                    <div
                        ref={ref}
                        id="pdf-image"
                        className={`print-preview pdf-image ${deletedClass}`}
                        onClick={(e) => !isLocked && !isDocumentLoading && activeImage(e)}
                        data-deleted-param={deleted}
                        data-docsecid={documentSectionId}
                        data-index={index}
                        data-image={pageImage}
                        data-docname={docName}
                        data-docparentid={parentId}
                        data-deleted={deletedImage}
                    >
                        <i className="fa-solid fa-xmark" />
                        {!isLocked && !isDocumentLoading && (
                            <div
                                className="separator_cut aaa"
                                onClick={(e) => setSeparator(e, index, documentSectionId)}
                                style={{ display: "block" }}
                            >
                                <i className="fas fa-cut"></i>
                            </div>
                        )}
                        <span className="image-checkbox">
                            <i className="fas fa-check-square"></i>
                        </span>

                        {isVisible ? (
                            !isLocked ? (
                                <HtmlTooltip
                                    placement="right"
                                    title={
                                        <img
                                            width={374}
                                            height={460}
                                            src={pageImage}
                                            alt="pdf-page"
                                        />
                                    }
                                >
                                    <img
                                        style={getItemStyleInner(snapshot.isDragging)}
                                        className="multiple-edit-image"
                                        data-docsecid={documentSectionId}
                                        data-index={index}
                                        src={pageImage}
                                        data-deleted-param={deleted}
                                        alt="pdf-page"
                                    />
                                </HtmlTooltip>
                            ) : (
                                <img
                                    style={getItemStyleInner(snapshot.isDragging)}
                                    className="multiple-edit-image"
                                    data-docsecid={documentSectionId}
                                    data-index={index}
                                    src={pageImage}
                                    data-deleted-param={deleted}
                                    alt="pdf-page"
                                />
                            )
                        ) : (
                            <div className="pdf-skeleton-tile" />
                        )}

                        {!isLocked && !deletedImage && isVisible && (
                            <button
                                className="thumb-zoom"
                                title="Zoom"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomPage(item);
                                    setZoomOpen(true);
                                }}
                            >
                                <i className="fa-solid fa-magnifying-glass-plus"></i>
                            </button>
                        )}
                        <p>Page {index + 1}</p>
                    </div>
                </div>
            )}
        </Draggable>
    );
};

// Re-render a row only when something visible to it actually changes.
// The parent re-creates handler functions (activeImage, setSeparator, etc.)
// on every render, so we compare only the props that affect what this row
// displays. This keeps the Split/Merge page responsive while dozens of
// thumbnails are streaming in.
const arePropsEqual = (prev, next) => {
    if (prev.pageImage !== next.pageImage) return false;
    if (prev.index !== next.index) return false;
    if (prev.documentSectionId !== next.documentSectionId) return false;
    if (prev.docName !== next.docName) return false;
    if (prev.parentId !== next.parentId) return false;
    if (prev.isLocked !== next.isLocked) return false;
    if (prev.isDocumentLoading !== next.isDocumentLoading) return false;
    if (prev.k !== next.k) return false;
    if (prev.deletedPages !== next.deletedPages) return false;
    if (prev.item !== next.item) return false;
    return true;
};

const DraggablePage = React.memo(DraggablePageImpl, arePropsEqual);

export default DraggablePage;
